import { gql, useMutation, useQuery } from '@apollo/client';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogTitle,
  Button,
  ButtonGroup,
} from '@apollo/orbit';
import React, { useCallback, useRef, useState } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { CommentStatus } from 'src/lib/graphqlTypes/types';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';

import { GraphRef } from '../../../hooks/useGraphRef';
import {
  proposalChangesRouteConfig,
  proposalEditorRouteConfig,
  proposalHomepageRouteConfig,
} from '../../../routes';
import {
  UseMentionUsersServiceFragment,
  useGetUsersForMentions,
} from '../../CommentMarkdownEditor/useMentionUsers';
import {
  SingleProposalComment,
  SingleProposalCommentFragment,
} from '../SingleProposalComment';

const DeleteCommentModalParentChangeCommentFragment = gql`
  fragment DeleteCommentModalParentChangeCommentFragment on ParentChangeProposalComment {
    id
    status
    message
    replyCount
  }
`;

const DeleteCommentModalParentGeneralCommentFragment = gql`
  fragment DeleteCommentModalParentGeneralCommentFragment on ParentGeneralProposalComment {
    id
    status
    message
    replyCount
  }
`;

export const DeleteCommentModal = ({
  graphRef,
  isOpen,
  onClose,
}: {
  graphRef: GraphRef;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { proposalCommentId, commentThreadId } = useRouteParams(
    proposalHomepageRouteConfig,
    proposalEditorRouteConfig,
    proposalChangesRouteConfig,
    catchAllRouteConfig,
  );
  const cancelRef = useRef<HTMLButtonElement>(null);

  if (isOpen && !commentThreadId) {
    throw new Error('Comment thread id is required for this modal');
  }

  const { data } = useQuery<
    GraphQLTypes.DeleteProposalCommentQuery,
    GraphQLTypes.DeleteProposalCommentQueryVariables
  >(
    gql`
      query DeleteProposalCommentQuery(
        $graphId: ID!
        $graphVariant: String!
        $commentId: ID!
      ) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            proposal {
              id
              comment(id: $commentId) {
                ... on NotFoundError {
                  message
                }
                ... on ParentChangeProposalComment {
                  id
                  ...SingleProposalCommentFragment
                }
                ... on ReplyChangeProposalComment {
                  id
                  ...SingleProposalCommentFragment
                }
                ... on ParentGeneralProposalComment {
                  id
                  ...SingleProposalCommentFragment
                }
                ... on ReplyGeneralProposalComment {
                  id
                  ...SingleProposalCommentFragment
                }
                ... on ReviewProposalComment {
                  id
                  ...SingleProposalCommentFragment
                }
              }
            }
          }
          ...UseMentionUsersServiceFragment
        }
      }
      ${SingleProposalCommentFragment}
      ${UseMentionUsersServiceFragment}
    `,
    {
      variables: {
        ...graphRef,
        commentId: proposalCommentId as string,
      },
      skip: !proposalCommentId,
    },
  );

  const [deleteComment] = useMutation<
    GraphQLTypes.DeleteCommentModalDeleteCommentMutation,
    GraphQLTypes.DeleteCommentModalDeleteCommentMutationVariables
  >(gql`
    mutation DeleteCommentModalDeleteCommentMutation(
      $input: DeleteCommentInput!
      $graphId: ID!
      $graphVariant: String!
    ) {
      graph(id: $graphId) {
        variant(name: $graphVariant) {
          proposal {
            ... on ProposalMutation {
              deleteComment(input: $input) {
                ... on DeleteCommentSuccess {
                  comment {
                    ...DeleteCommentModalParentGeneralCommentFragment
                    ...DeleteCommentModalParentChangeCommentFragment
                  }
                }
                ... on NotFoundError {
                  message
                }
                ... on PermissionError {
                  message
                }
                ... on ValidationError {
                  message
                }
              }
            }
          }
        }
      }
    }
    ${DeleteCommentModalParentChangeCommentFragment}
    ${DeleteCommentModalParentGeneralCommentFragment}
  `);

  const graph = data?.service;
  const comment = graph?.variant?.proposal?.comment;
  const isGeneralComment =
    comment?.__typename === 'ParentGeneralProposalComment' ||
    comment?.__typename === 'ReplyGeneralProposalComment';

  const handleSubmit = useCallback(async () => {
    if (
      !proposalCommentId ||
      !commentThreadId ||
      comment?.__typename === 'NotFoundError'
    ) {
      return;
    }
    setIsSubmitting(true);
    const { data: responseData } = await deleteComment({
      variables: {
        graphId: graphRef.graphId,
        graphVariant: graphRef.graphVariant,
        input: { id: proposalCommentId },
      },
      update: (cache, { data: resultData }) => {
        const resultProposal = resultData?.graph?.variant?.proposal;
        if (
          resultProposal?.__typename === 'ProposalMutation' &&
          resultProposal?.deleteComment.__typename === 'DeleteCommentSuccess' &&
          resultProposal?.deleteComment.comment === null
        ) {
          const parentCommentRef = cache.identify({
            __typename: isGeneralComment
              ? 'ParentGeneralProposalComment'
              : 'ParentChangeProposalComment',
            id: commentThreadId,
          });
          if (proposalCommentId === commentThreadId) {
            // ? Note: When we receive null from the backend for the deleteComment mutation
            // ? for a parent comment, that means it is fully deleted and not soft
            // ? deleted (i.e. shows the text "This has been deleted") so we should
            // ? remove the comment thread from the cache after deletion.
            cache.evict({ id: parentCommentRef });
          } else {
            const parentCommentFromCache = cache.readFragment<
              | GraphQLTypes.DeleteCommentModalParentGeneralCommentFragment
              | GraphQLTypes.DeleteCommentModalParentChangeCommentFragment
            >({
              id: parentCommentRef,
              fragment: isGeneralComment
                ? DeleteCommentModalParentGeneralCommentFragment
                : DeleteCommentModalParentChangeCommentFragment,
            });
            // ? Note: removing the last reply from a thread will delete the entire
            // ? thread itself so if this is the case we should evict the parent comment
            // ? Otherwise we should just modify the cache to remove the reply from the list
            if (
              parentCommentFromCache?.status === CommentStatus.DELETED &&
              parentCommentFromCache.replyCount === 1
            ) {
              cache.evict({ id: parentCommentRef });
            } else {
              cache.modify<
                Omit<GraphQLTypes.ParentChangeProposalComment, '__typename'> &
                  Omit<GraphQLTypes.ParentGeneralProposalComment, '__typename'>
              >({
                id: parentCommentRef,
                fields: {
                  replyCount: (currentReplyCount = 0) => currentReplyCount - 1,
                  replies: (existingReplyRefs = [], { readField }) =>
                    existingReplyRefs.filter(
                      (ref) => proposalCommentId !== readField('id', ref),
                    ),
                },
              });
            }
            cache.gc();
          }
        }
      },
    });

    if (
      responseData?.graph?.variant?.proposal.__typename ===
        'ProposalMutation' &&
      responseData?.graph?.variant?.proposal.deleteComment.__typename ===
        'DeleteCommentSuccess'
    ) {
      onClose();
      setErrorMessage(undefined);
    } else {
      setErrorMessage('An error has occurred');
    }
    setIsSubmitting(false);
  }, [
    comment?.__typename,
    commentThreadId,
    deleteComment,
    graphRef.graphId,
    graphRef.graphVariant,
    isGeneralComment,
    onClose,
    proposalCommentId,
  ]);

  const usersForMentions = useGetUsersForMentions(graph);

  return (
    <AlertDialog
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay />
      <AlertDialogContent>
        {!graph || !comment ? (
          <AlertDialogBody className="py-20">
            <Loading />
          </AlertDialogBody>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete comment?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody>
              <p>This cannot be undone.</p>
              {errorMessage && <ErrorMessage />}
              {comment?.__typename !== 'NotFoundError' ? (
                <div className="rounded border border-primary p-4">
                  <SingleProposalComment
                    usersForMentions={usersForMentions}
                    comment={comment}
                    commentThreadId={commentThreadId}
                    shouldShowActions={false}
                    shouldClampText
                    shouldShowTimestamp={false}
                  />
                </div>
              ) : null}
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button variant="secondary" onClick={onClose} ref={cancelRef}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  loadingText="Deleting"
                >
                  Delete
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
